import { useNavigate } from 'react-router-dom'

import { Box, Button, Card, Link, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { BenefitsBankAccountCardFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

const getLastFourDigits = (accountNumber: string) => accountNumber.slice(-4)

const BenefitsBankAccountCardSkeleton = () => {
  return (
    <Box
      data-testid="benefits-bank-account-card-skeleton"
      width="s300"
      height="s200"
      gap="s12"
      border={{
        all: {
          width: 's1',
          color: 'grey400',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs012',
      }}
      paddingTop="s24"
      paddingLeft="s12"
    >
      <Skeleton width="s240" height="s24" />
      <Box flexDirection="row" gap="s12">
        <Skeleton width="s120" height="s16" />
        <Skeleton width="s48" height="s16" />
      </Box>

      <Box paddingTop="s12">
        <Skeleton width="s200" height="s24" />
      </Box>

      <Skeleton width="s120" height="s24" />
    </Box>
  )
}

const BenefitsBankAccountCard = ({
  availableBalanceCents,
  accountAlias,
  accountNumber,
  id,
  cardholdersConnection,
}: BenefitsBankAccountCardFragment) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    access: { locale },
    company: { currency },
  } = useCurrentSession()

  const title = accountAlias || getLastFourDigits(accountNumber) || ''
  const bankAccountUrl = navigation.finance.banking.accounts.accountId.transactions.fullPath({
    id,
  })

  const bankAccountCards = navigation.finance.banking.accounts.accountId.cards.fullPath({
    id,
  })

  const numberOfBenefitCards = cardholdersConnection?.edges?.reduce(
    (totalCards, cardholderEdge) => {
      const salaryCards = cardholderEdge.node.cards.filter(
        (card) => card.creationSource === 'salary'
      )
      return totalCards + salaryCards.length
    },
    0
  )

  const totalCardCount = numberOfBenefitCards || 0
  const companyCurrency = currency || 'EUR'

  const handleCardClick = () => {
    track(trackingEvents.benefits.openBankAccountCards)
    navigate(bankAccountCards)
  }

  const handleAccountClick = () => {
    track(trackingEvents.benefits.openBankAccount)
    navigate(bankAccountUrl)
  }

  return (
    <Box flexGrow maxWidth="s360" width="s240">
      <Card>
        <Card.Content>
          <Box gap="s12">
            <Box gap="s4">
              <Text.Normal color="textPrimary" size="300" weight="semibold">
                {title}
              </Text.Normal>

              <Box flexDirection="row" gap="s6">
                <Text.Normal color="grey800" size="200">
                  {i18n.t('benefits.homepage.bank_account.cards_account')} ·{' '}
                </Text.Normal>
                <Link.Touchable onClick={handleCardClick}>
                  {totalCardCount} {i18n.t('benefits.homepage.bank_account.cards')}
                </Link.Touchable>
              </Box>
            </Box>

            <Text.Header2 color="grey1000" weight="semibold">
              {formattedAmount({
                amountCents: availableBalanceCents,
                currency: companyCurrency,
                locale,
              })}
            </Text.Header2>
            <Box paddingTop="s12">
              <Button iconName="external link" type="secondary" onClick={handleAccountClick}>
                {i18n.t('benefits.homepage.bank_account.navigate')}
              </Button>
            </Box>
          </Box>
        </Card.Content>
      </Card>
    </Box>
  )
}
export { BenefitsBankAccountCard, getLastFourDigits, BenefitsBankAccountCardSkeleton }
